import { readonlyStringListQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { readonlyIdentifierListQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { choiceQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { Stack } from "@mui/material";
import { DEFAULT_WINDOW_MINUTES } from "components/manualReview/hooks/useManualReviewNavigation";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenant } from "hooks/settings";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useEffect, useMemo } from "react";
import { useGetSegmentStatusListQuery } from "src/store/api/generatedApi";
import { DayWindowTimeline } from "../DayWindowTimeline";
import { useDayWindowNavigation } from "../useDayWindowNavigation";
import { MultiFeedVideoPlayer } from "./components/MultiFeedVideoPlayer";
import {
  VideoSourceSelector,
  doesSourceHaveFeeds,
  useVideoSources,
} from "./components/VideoSourceSelector";
import { HotkeyProvider } from "./contexts/HotkeyContext";
import {
  VideoControlProvider,
  useVideoControlActions,
} from "./contexts/VideoControlContext";
import { PLAYBACK_SCOPE } from "components/scrapEyeDataDashboard/VideoPlayer/constants";

dayjs.extend(utc);

const HALF_WINDOW_MINUTES = DEFAULT_WINDOW_MINUTES / 2;
const WINDOW_DURATION_SECONDS = DEFAULT_WINDOW_MINUTES * 60;
const HALF_WINDOW_SECONDS = HALF_WINDOW_MINUTES * 60;
const COVERAGE_SEGMENT_LENGTH_SEC = 15;
const COVERAGE_SEGMENTS_PER_DAY = (24 * 60 * 60) / COVERAGE_SEGMENT_LENGTH_SEC;

type Props = Readonly<{
  deviceId: number;
  deviceName: string;
}>;

export const VideoPlayerPanel = ({ deviceId, deviceName }: Props) => {
  return (
    <HotkeyProvider>
      <VideoControlProvider>
        <VideoPlayerPanelInner deviceId={deviceId} deviceName={deviceName} />
      </VideoControlProvider>
    </HotkeyProvider>
  );
};

const VideoPlayerPanelInner = ({ deviceId, deviceName }: Props) => {
  const actions = useVideoControlActions();
  const tenantName = useTenant();
  const [sourceMode, setSourceMode] = useNavigationQuery(
    "videoSource",
    "angle",
    choiceQuery(["angle", "camera", "angle_group"] as const),
    "replace"
  );
  const [selectedAngleIds, setSelectedAngleIds] = useNavigationQuery(
    "videoAngles",
    [],
    readonlyIdentifierListQuery,
    "replace"
  );
  const [selectedCameraPhysicalIds, setSelectedCameraPhysicalIds] =
    useNavigationQuery("videoCameras", [], readonlyStringListQuery, "replace");
  const nav = useDayWindowNavigation("videoAt", () =>
    dayjs.utc().subtract(1, "hour")
  );
  const { windowStart, windowEnd } = nav;

  const availabilityAtTime = useMemo(
    () => windowStart.startOf("hour").toISOString(),
    [windowStart]
  );

  const coverageSelection = useMemo(() => {
    switch (sourceMode) {
      case "angle":
        return selectedAngleIds.length > 0
          ? { cameraAngleIds: [...selectedAngleIds] }
          : null;
      case "camera":
        return selectedCameraPhysicalIds.length > 0
          ? { cameraPhysicalIds: [...selectedCameraPhysicalIds] }
          : null;
      case "angle_group":
        // No per-angle-group coverage scoping yet; the bar is hidden in this
        // mode. Wire this case up when angle_group is exposed in the selector.
        return null;
    }
  }, [sourceMode, selectedAngleIds, selectedCameraPhysicalIds]);

  const { data: segmentCoverage } = useGetSegmentStatusListQuery(
    coverageSelection
      ? {
          tenantName,
          deviceName,
          segmentType: "video",
          start: nav.dayStartIso,
          count: COVERAGE_SEGMENTS_PER_DAY,
          segmentLengthSec: COVERAGE_SEGMENT_LENGTH_SEC,
          ...coverageSelection,
        }
      : skipToken
  );

  useEffect(() => {
    actions.reset();
    actions.updateDuration(WINDOW_DURATION_SECONDS);
    actions.seekTo(HALF_WINDOW_SECONDS);
  }, [windowStart, windowEnd, actions]);

  const videoSource = useVideoSources({
    deviceId,
    mode: sourceMode,
    selectedAngleIds,
    selectedCameraPhysicalIds,
    atTime: availabilityAtTime,
  });

  const hasVideoSources = doesSourceHaveFeeds(videoSource);

  return (
    <Stack p={2} spacing={2}>
      <DayWindowTimeline
        nav={nav}
        segmentCoverageItems={segmentCoverage?.items}
        segmentLengthSec={COVERAGE_SEGMENT_LENGTH_SEC}
      />
      <VideoSourceSelector
        deviceId={deviceId}
        mode={sourceMode}
        onModeChange={setSourceMode}
        selectedAngleIds={selectedAngleIds}
        onAngleIdsChange={setSelectedAngleIds}
        selectedCameraPhysicalIds={selectedCameraPhysicalIds}
        onCameraPhysicalIdsChange={setSelectedCameraPhysicalIds}
        atTime={availabilityAtTime}
      />
      {hasVideoSources ? (
        <MultiFeedVideoPlayer
          hotkeyScope={PLAYBACK_SCOPE}
          source={videoSource}
          startIso={windowStart.toISOString()}
          endIso={windowEnd.toISOString()}
          videoStart={windowStart}
        />
      ) : null}
    </Stack>
  );
};
